import React, { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    // SEO: Title
    document.title = "Privacy Policy — ai4hack";

    // SEO: Meta description
    const metaDesc =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
        return m;
      })();
    metaDesc.setAttribute(
      "content",
      "Privacy Policy for ai4hack — how we collect, use, and protect your data for the ai4hack AI Hackathon. Last updated August 11, 2025."
    );

    // SEO: Canonical
    const canonicalHref = "https://ai4hack.com/privacy-policy";
    const existingCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (existingCanonical) {
      existingCanonical.href = canonicalHref;
    } else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonicalHref);
      document.head.appendChild(link);
    }
  }, []);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ai4hack.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Privacy Policy",
        item: "https://ai4hack.com/privacy-policy",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-beige text-foreground">
      <Navbar />

      <main id="main-content" className="relative pt-28 pb-16 overflow-x-hidden">
        <div className="pointer-events-none absolute -top-10 -right-10 w-1/2 h-[40%] opacity-20 blur-3xl rounded-full" style={{ background: 'linear-gradient(180deg, rgba(242,76,39,0.8) 0%, rgba(242,76,39,0) 100%)' }}></div>
        <section className="section-container">
          <article className="mx-auto max-w-3xl">
            <header className="mb-8 text-center md:text-left">
              <div className="flex justify-center md:justify-start mb-3">
                <div
                  className="inline-flex items-center justify-center px-4 py-2 rounded-full"
                  style={{
                    backgroundColor: 'rgba(242, 76, 39, 0.1)',
                    border: '1px solid rgba(242, 76, 39, 0.2)',
                    color: 'rgba(242, 76, 39, 1)',
                  }}
                >
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white mr-2"
                    style={{ backgroundColor: 'rgba(242, 76, 39, 1)' }}
                  >
                    9
                  </span>
                  <span>Privacy Policy</span>
                </div>
              </div>
              <h1
                className="text-3xl sm:text-4xl md:text-5xl font-display font-bold tracking-tight"
                style={{
                  background: 'linear-gradient(135deg, rgba(242, 76, 39, 1) 0%, rgba(31, 31, 30, 1) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Privacy Policy — ai<span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span>hack
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">Last Updated: August 11, 2025</p>
            </header>

            <hr className="my-8 border-border" />

            <section className="space-y-6">
              <p>
                ai4hack (“we”, “our”, “us”) operates the website ai4hack.com (the “Site”) and organizes the ai4hack AI Hackathon (the “Event”). We respect your privacy and are committed to protecting your personal data.
              </p>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">1. Information We Collect</h2>
                <p className="mt-4">When you visit our Site, register for the Event, or interact with us, we may collect:</p>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>
                    <strong>Personal Identification Information</strong> — Name, email address, phone number, social media handles, organization, job title, country of residence.
                  </li>
                  <li>
                    <strong>Application Data</strong> — Resume/CV, portfolio links, project submissions, GitHub/LinkedIn profiles, and other information you share in your Event application.
                  </li>
                  <li>
                    <strong>Technical Data</strong> — IP address, browser type, operating system, referring URLs, and usage data via cookies and analytics tools.
                  </li>
                  <li>
                    <strong>Media Content</strong> — Photos, videos, and recordings taken during the Event.
                  </li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">2. How We Use Your Information</h2>
                <p className="mt-4">We use your data to:</p>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>Process and review your Event application.</li>
                  <li>Communicate with you about participation, schedules, and updates.</li>
                  <li>Promote the Event (including photos and videos taken during the hackathon).</li>
                  <li>Analyze participation trends and improve future events.</li>
                  <li>Comply with legal obligations.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">3. Sharing Your Information</h2>
                <p className="mt-4">We may share your information with:</p>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>Event partners, sponsors, and jurors for reviewing applications, judging projects, and networking purposes.</li>
                  <li>Service providers (e.g., email platforms, hosting, analytics tools) to help operate the Event.</li>
                  <li>Legal authorities when required by law.</li>
                </ul>
                <p className="mt-3">We do not sell your data to third parties.</p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">4. Cookies &amp; Analytics</h2>
                <p className="mt-4">
                  We use cookies and analytics tools (e.g., Google Analytics) to enhance your browsing experience and understand how our Site is used. You can disable cookies in your browser settings, but some features may not work properly.
                </p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">5. Data Retention</h2>
                <p className="mt-4">We store personal data only as long as necessary for the purposes described above, unless a longer period is required by law.</p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">6. Your Rights</h2>
                <p className="mt-4">Depending on your location, you may have the right to:</p>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>Access the data we hold about you.</li>
                  <li>Request correction or deletion of your data.</li>
                  <li>Object to the processing of your data.</li>
                  <li>Withdraw consent at any time (without affecting prior lawful processing).</li>
                </ul>
                <p className="mt-3">
                  To make a request, email us at <a className="text-primary underline" href="mailto:hi@ai4hack.com">hi@ai4hack.com</a>.
                </p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">7. Security</h2>
                <p className="mt-4">We take reasonable technical and organizational measures to protect your personal data from unauthorized access, loss, or misuse.</p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">8. Third-Party Links</h2>
                <p className="mt-4">Our Site may link to external websites. We are not responsible for the privacy practices or content of those sites.</p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">9. Changes to This Policy</h2>
                <p className="mt-4">We may update this Privacy Policy from time to time. The latest version will always be posted here with the “Last Updated” date.</p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">10. Contact Us</h2>
                <p className="mt-4">If you have any questions about this Privacy Policy or our data practices, contact us at:</p>
                  <ul className="mt-3 list-none pl-0 space-y-1">
                    <li>📍 730 Moreno Ave, Palo Alto, CA, 94303, US</li>
                    <li>📧 <a className="text-primary underline" href="mailto:hi@ai4hack.com">hi@ai4hack.com</a></li>
                    <li>🌍 <a className="text-primary underline" href="https://ai4hack.com" target="_blank" rel="noopener noreferrer">ai4hack.com</a></li>
                  </ul>
              </section>
            </section>
          </article>
        </section>
      </main>

      {/* Structured data */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
